import { useId, useState, type FormEvent } from 'react';
import type { PortalEditorView } from '../../../lib/api';
import { FIELD, LABEL, PRIMARY, Refusals, todayIso, type SaveChange } from './editorShared';

/**
 * VP9 - a dated timeline entry about one or more deals. Entries are ADDED here, never rewritten: the roster's entries
 * are the record of what happened. The client sees a new one on the timeline, marked New.
 */
export default function TimelineEntryForm({ view, onSave }: { view: PortalEditorView; onSave: SaveChange }) {
  const id = useId();
  const [date, setDate] = useState(todayIso());
  const [kind, setKind] = useState(view.timelineKinds.includes('update') ? 'update' : view.timelineKinds[0] ?? 'update');
  const [title, setTitle] = useState('');
  const [filter, setFilter] = useState('');
  const [dealIds, setDealIds] = useState<string[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [saved, setSaved] = useState('');
  const [saving, setSaving] = useState(false);

  const needle = filter.trim().toLowerCase();
  const shown = view.deals.filter(
    (d) => dealIds.includes(d.id) || !needle || `${d.name} ${d.city ?? ''}`.toLowerCase().includes(needle)
  );
  const toggle = (dealId: string) =>
    setDealIds((xs) => (xs.includes(dealId) ? xs.filter((x) => x !== dealId) : [...xs, dealId]));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    const refused = await onSave({ kind: 'timelineAdd', fields: { date, kind, title, dealIds } });
    setSaving(false);
    if (refused) {
      setErrors(refused);
      setSaved('');
      return;
    }
    setErrors([]);
    setTitle('');
    setDealIds([]);
    setFilter('');
    setSaved('Added - the client will see it on the timeline, marked New.');
  };

  return (
    <form onSubmit={submit} aria-label="Add a timeline entry" className="space-y-3">
      <h3 className="text-sm font-semibold text-gray-900">Add a timeline entry</h3>
      <div className="grid gap-3 sm:grid-cols-[180px_180px_1fr]">
        <div>
          <label className={LABEL} htmlFor={`${id}-date`}>
            Date
          </label>
          <input id={`${id}-date`} type="date" value={date} onChange={(e) => setDate(e.target.value)} className={FIELD} />
        </div>
        <div>
          <label className={LABEL} htmlFor={`${id}-kind`}>
            Kind
          </label>
          <select id={`${id}-kind`} value={kind} onChange={(e) => setKind(e.target.value)} className={FIELD}>
            {view.timelineKinds.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={LABEL} htmlFor={`${id}-title`}>
            What happened
          </label>
          <input id={`${id}-title`} value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} className={FIELD} />
        </div>
      </div>
      <div>
        <label className={LABEL} htmlFor={`${id}-find`}>
          About (one or more deals)
        </label>
        <input id={`${id}-find`} value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter by name or city" className={FIELD} />
        <div className="mt-2 max-h-44 overflow-auto border border-gray-200 rounded-lg p-2 space-y-1">
          {shown.map((d) => (
            <label key={d.id} className="flex items-center gap-2 text-sm text-gray-900">
              <input type="checkbox" checked={dealIds.includes(d.id)} onChange={() => toggle(d.id)} />
              {d.name}
              {d.city ? ` - ${d.city}` : ''}
            </label>
          ))}
          {!shown.length && <p className="text-xs text-gray-500">No deal matches.</p>}
        </div>
      </div>
      <button type="submit" disabled={saving} className={PRIMARY}>
        {saving ? 'Adding...' : 'Add entry'}
      </button>
      <Refusals errors={errors} />
      {saved && (
        <p role="status" className="text-sm text-success">
          {saved}
        </p>
      )}
    </form>
  );
}
